import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useRef,
  useState,
} from 'react'

type Store = {
  sessionId: string
  isSubmitted: boolean
  isInActive: boolean
  isTokenAvailable: boolean
  setSessionId: Dispatch<SetStateAction<string>>
  setIsSubmitted: Dispatch<SetStateAction<boolean>>
  setIsInActive: Dispatch<SetStateAction<boolean>>
  setIsTokenAvailable: Dispatch<SetStateAction<boolean>>
  handleCompleteActivity?: () => void
  isDisabledWorkspace: boolean
  setIsDisabledWorkspace: Dispatch<SetStateAction<boolean>>
  invitationRef: React.RefObject<HTMLDivElement> | null
  employeeDetailsBtnRef: React.RefObject<HTMLDivElement> | null
  employeeDetailsRef: HTMLDivElement | null
  setEmployeeDetailsRef: Dispatch<SetStateAction<HTMLDivElement | null>>
  programCreateBtnRef: React.RefObject<HTMLButtonElement> | null
  firstProgramTileRef: React.RefObject<HTMLDivElement> | null
  firstProgressTrackerRef: React.RefObject<HTMLDivElement> | null
  createTaskBtnRef: React.RefObject<HTMLButtonElement> | null
  taskModalRef: HTMLDivElement | null
  setTaskModalRef: Dispatch<SetStateAction<HTMLDivElement | null>>
  taskDetailsBtnRef: React.RefObject<HTMLButtonElement> | null
  firstTaskDetailsBtnRef: HTMLDivElement | null
  setFirstTaskDetailsBtnRef: Dispatch<SetStateAction<HTMLDivElement | null>>
  teamRef: React.RefObject<HTMLDivElement> | null
  libraryRef: React.RefObject<HTMLDivElement> | null
  isPowerfulTourOpen: boolean
  setIsPowerfulTourOpen: Dispatch<SetStateAction<boolean>>
  isExceedingPlanWorkspace: boolean
  setIsExceedingPlanWorkspace: Dispatch<SetStateAction<boolean>>
}

export const NotificationContext = createContext<Store>({
  sessionId: '',
  isSubmitted: false,
  isInActive: false,
  setSessionId: () => {},
  setIsSubmitted: () => {},
  setIsInActive: () => {},
  isTokenAvailable: false,
  setIsTokenAvailable: () => {},
  isDisabledWorkspace: false,
  setIsDisabledWorkspace: () => {},
  invitationRef: null,
  employeeDetailsBtnRef: null,
  employeeDetailsRef: null,
  setEmployeeDetailsRef: () => {},
  programCreateBtnRef: null,
  firstProgramTileRef: null,
  firstProgressTrackerRef: null,
  createTaskBtnRef: null,
  taskModalRef: null,
  setTaskModalRef: () => {},
  taskDetailsBtnRef: null,
  firstTaskDetailsBtnRef: null,
  setFirstTaskDetailsBtnRef: () => {},
  teamRef: null,
  libraryRef: null,
  isPowerfulTourOpen: false,
  setIsPowerfulTourOpen: () => {},
  isExceedingPlanWorkspace: false,
  setIsExceedingPlanWorkspace: () => {},
})

type Props = {
  defaultSessionId?: string
  children: ReactNode
  handleCompleteActivity?: () => void
}

function NotificationProvider(props: Props) {
  const { defaultSessionId = '', children, handleCompleteActivity } = props

  const [isTokenAvailable, setIsTokenAvailable] = useState(true)
  const [isDisabledWorkspace, setIsDisabledWorkspace] = useState(false)
  const [isExceedingPlanWorkspace, setIsExceedingPlanWorkspace] =
    useState(false)
  const [sessionId, setSessionId] = useState(defaultSessionId)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isInActive, setIsInActive] = useState(false)

  //powerful tour
  const [isPowerfulTourOpen, setIsPowerfulTourOpen] = useState(false)

  //refs
  const invitationRef = useRef<HTMLDivElement | null>(null)
  const employeeDetailsBtnRef = useRef<HTMLDivElement | null>(null)
  const programCreateBtnRef = useRef<HTMLButtonElement | null>(null)
  const firstProgramTileRef = useRef<HTMLDivElement | null>(null)
  const firstProgressTrackerRef = useRef<HTMLDivElement | null>(null)
  const createTaskBtnRef = useRef<HTMLButtonElement | null>(null)
  const teamRef = useRef<HTMLDivElement | null>(null)
  const libraryRef = useRef<HTMLDivElement | null>(null)
  const taskDetailsBtnRef = useRef<HTMLButtonElement | null>(null)
  const [firstTaskDetailsBtnRef, setFirstTaskDetailsBtnRef] =
    useState<HTMLDivElement | null>(null)
  const [taskModalRef, setTaskModalRef] = useState<HTMLDivElement | null>(null)
  const [employeeDetailsRef, setEmployeeDetailsRef] =
    useState<HTMLDivElement | null>(null)

  const value: Store = {
    sessionId,
    setSessionId,
    handleCompleteActivity,
    isSubmitted,
    setIsSubmitted,
    isInActive,
    setIsInActive,
    isTokenAvailable,
    setIsTokenAvailable,
    isDisabledWorkspace,
    setIsDisabledWorkspace,
    invitationRef,
    employeeDetailsBtnRef,
    employeeDetailsRef,
    setEmployeeDetailsRef,
    programCreateBtnRef,
    firstProgramTileRef,
    firstProgressTrackerRef,
    createTaskBtnRef,
    taskModalRef,
    setTaskModalRef,
    taskDetailsBtnRef,
    firstTaskDetailsBtnRef,
    setFirstTaskDetailsBtnRef,
    teamRef,
    libraryRef,
    isPowerfulTourOpen,
    setIsPowerfulTourOpen,
    isExceedingPlanWorkspace,
    setIsExceedingPlanWorkspace,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export default NotificationProvider
